import type * as ClassTypes from './types/class';
import type {IAnimationOptions} from '@/components/animation/types';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from './types/observer';
import type {IValidation} from '@/components/form/validation/types';
import Observer from './observer';
import popupContentTemplate from '@/components/form/form-parametric-content.twig';
import {Utils} from './utils';
import Validation from '@/components/form/validation';

const validation: IValidation = new Validation();
const observer: IObserver = new Observer();

// Поведение шапки
export const initHeader = (headerWrap: HTMLElement): void => {
    import(/* webpackChunkName: "header" */ '@/components/header')
        .then(({default: Header}: IDynamicImport) => {
            const targetScroll: HTMLElement[] = [...document.querySelectorAll('.j-main-screen')] as HTMLElement[];
            const header: ClassTypes.IHeader = new Header();

            header.init({
                headerWrap,
                targetScroll
            });
        });
};

// Подключение туглеров
export const initTogglers = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "toggle" */ '@/components/toggle')
        .then(({default: Toggle}: IDynamicImport) => {
            new Toggle(wrapper).init();
        });
};

// Carousel
export const initCarousel = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "carousel" */ '@/components/carousel')
        .then(({default: Carousel}: IDynamicImport) => {
            new Carousel().init({
                wrapper,
                carouselSettings: options
            });
        });
};

// first-screen
// eslint-disable-next-line
export const initFirstScreenSlider = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "slider" */ '@/sections/first-screen')
        .then(({default: FirstScreenSlider}: IDynamicImport) => {
            new FirstScreenSlider().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Слайдер
export const initSlider = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "slider" */ '@/sections/slider')
        .then(({default: Slider}: IDynamicImport) => {
            new Slider().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Слайдер mini
export const initSliderMini = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
        .then(({default: SliderMini}: IDynamicImport) => {
            new SliderMini().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Слайдер content
// eslint-disable-next-line
export const initContentSlider = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "content-slider" */ '@/sections/content-slider')
        .then(({default: ContentSlider}: IDynamicImport) => {
            new ContentSlider().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Слайдер reviews
// eslint-disable-next-line
export const initReviewsSlider = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "reviews-slider" */ '@/sections/reviews-slider')
        .then(({default: ReviewsSlider}: IDynamicImport) => {
            new ReviewsSlider().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Слайдер carousel
// eslint-disable-next-line
export const initCarouselSlider = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "carousel" */ '@/sections/carousel')
        .then(({default: CarouselSlider}: IDynamicImport) => {
            new CarouselSlider().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// Слайдер вертикальный
// eslint-disable-next-line
export const initSliderVertical = (wrapper: HTMLElement, options: Record<string, boolean | string | number> = {}): void => {
    import(/* webpackChunkName: "slider" */ '@/sections/slider-vertical')
        .then(({default: SliderVertical}: IDynamicImport) => {
            new SliderVertical().init({
                wrapper,
                sliderSettings: options
            });
        });
};

// STICKY SLIDER
export const initStickySlider = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "sticky-slider" */ '@/sections/slider-vertical-sticky')
        .then(({default: StickySlider}: IDynamicImport) => {
            new StickySlider().init(wrapper);
        });
};

// Табы
export const initTabs = (elem: HTMLElement): void => {
    import(/* webpackChunkName: "tabs" */ '@/components/tabs')
        .then(({default: Tabs}: IDynamicImport) => {
            new Tabs().init({elem});
        });
};

export const initPopup = (buttons: HTMLElement[], otherOptions: Partial<ClassTypes.IPopupFacadeOptions> = {}): void => {
    import(/* webpackChunkName: "popup-facade" */ '@/components/popup/popupFacade')
        .then(({default: PopupFacade}: IDynamicImport) => {
            const options: ClassTypes.IPopupFacadeOptions = {
                ...{openButtons: buttons},
                ...otherOptions
            };

            const t: ClassTypes.IPopupFacade = new PopupFacade(options);

            t.init();
        });
};

export const initPlanoplanWidget = (uid: string): void => {
    const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-brand-base') || null;

    window.Planoplan.init({
        uid,
        width          : '100%',
        height         : '100%',
        el             : 'planoplan',
        textColor      : '#000000',
        backgroundColor: '#ffffff',
        ...primaryColor && {
            primaryColor
        }
    });
};

export const insertPremisesData = (wrapper: HTMLElement, data: ClassTypes.IPopupIframeData): void => {
    const currentForm = wrapper.querySelector('.j-form') as HTMLElement;
    const container = currentForm?.querySelector('.j-form__parametric-content') as HTMLElement;

    if (!currentForm || !container) {
        return;
    }

    const fillField = currentForm.querySelector('.j-form__fill-field') as HTMLInputElement;

    if (fillField) {
        fillField.value = `${data.description}`;
    }

    Utils.clearHtml(container);
    Utils.insetContent(container, popupContentTemplate(data));
};


export const initVideoTabs = (wrapperSelector: string): void => {
    const wrapper = document.querySelector(wrapperSelector);

    if (wrapper) {
        const tabs: HTMLElement[] = Array.from(wrapper.querySelectorAll('.j-video__tab'));
        const iframe = wrapper.querySelector('.j-video__iframe') as HTMLIFrameElement;
        const isActive = 'is-active';

        tabs.forEach((tab: HTMLElement): void => {
            tab.addEventListener('click', (event: MouseEvent) => {
                const target = event.currentTarget as HTMLElement;

                if (!target || !iframe) {
                    return;
                }

                iframe.src = target.dataset['video'] || '';
                tabs.forEach((element: HTMLElement) => {
                    const method = element === target ? 'add' : 'remove';

                    element.classList[method](isActive);
                });
            });
        });
    }
};

// Анимация блоков при прокрутке (GSAP)
export const initAnimation = (options: Partial<IAnimationOptions> = {blockSelector: '.j-animation__section'}): void => {
    const animationWrapper = document.querySelector('.j-animation');

    if (animationWrapper) {
        import(/* webpackChunkName: "animation" */ '@/components/animation')
            .then(({default: Animation}: IDynamicImport) => {
                return new Animation({
                    wrapper: animationWrapper,
                    ...options
                });
            });
    }
};

// Инициализация loader
export const initLoader = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "loader" */ '@/components/loader')
        .then(({default: Loader}: IDynamicImport) => {
            new Loader().init({wrapper});
        });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = (): void => {
    observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
        if (!popup.content) {
            return;
        }

        // Форма
        const callbackFormElement = popup.content.querySelector('.j-form') as HTMLElement;

        if (callbackFormElement) {
            validation.update();
            import(/* webpackChunkName: "form" */ '@/components/form')
                .then(({default: Form}: IDynamicImport) => {
                    new Form().init({
                        target        : callbackFormElement,
                        popupInstance : popup,
                        successMessage: true
                    });
                });
        }
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons: HTMLElement[]): void => {
    const options = {
        removeDelay         : 300,
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify              : 'popup_theme_callback'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

export const initInfoPoint = (point: HTMLElement): void => {
    import(/* webpackChunkName: "infoPoint" */ '@/components/info-point')
        .then(({default: InfoPoint}: IDynamicImport) => {
            new InfoPoint(point).init();
        });
};
